import { EPSILON } from './constants.js';
import { closestPointOnSegment } from './sketch.js';
import type { SketchPoint } from './sketch.js';
import type { Mm } from './units.js';
import { quantise } from './units.js';

/**
 * Where a sketch point lands.
 *
 * Drawing an outline over work that already exists is the ordinary case: a
 * window traced against the brickwork it will be cut into, a second shape
 * sharing an edge with the first. Both only come out right if the point being
 * placed can find what is already there — and if the person placing it can see
 * what it found before letting go of the mouse. So a snap here is not just a
 * corrected position: it names what it caught, and the drawing says so.
 *
 * Brick snapping (`snap.ts`) is a different problem and stays separate. There,
 * a whole brick is being fitted against its neighbours with joints between
 * them; here, one point is being put on one place.
 */

/** What a point caught. */
export type SketchSnapKind =
  /** An end of a drawn line, or a corner of a drawn shape. */
  | 'point'
  /** The middle of a drawn line. */
  | 'midpoint'
  /** Anywhere along a drawn line. */
  | 'line'
  | 'brick-corner'
  | 'brick-edge'
  /** Held level with the point before it. */
  | 'level'
  /** Held plumb above or below the point before it. */
  | 'plumb';

/** Any line a point can be caught by: a drawn one, or the edge of a brick. */
export interface SnapLine {
  readonly a: SketchPoint;
  readonly b: SketchPoint;
  readonly id?: string;
}

export interface SketchSnap {
  readonly x: Mm;
  readonly y: Mm;
  readonly kind: SketchSnapKind;
  /** The line it locked onto, where it locked onto one. */
  readonly line?: SnapLine | undefined;
}

/** What each kind is called, where a person has to read it. */
export const SKETCH_SNAP_LABELS: Readonly<Record<SketchSnapKind, string>> = {
  point: 'point',
  midpoint: 'midpoint',
  line: 'on line',
  'brick-corner': 'brick corner',
  'brick-edge': 'brick edge',
  level: 'level',
  plumb: 'plumb',
};

export interface SketchSnapSources {
  /** Loose points: the run being drawn, and anything else worth catching. */
  readonly points?: readonly SketchPoint[] | undefined;
  /** Drawn lines, shape edges included. Their ends and middles count too. */
  readonly lines?: readonly SnapLine[] | undefined;
  readonly brickCorners?: readonly SketchPoint[] | undefined;
  readonly brickEdges?: readonly SnapLine[] | undefined;
  /** The point this one follows, for the level and plumb holds. */
  readonly from?: SketchPoint | null | undefined;
}

/**
 * Lowest wins, and distance decides within a tier.
 *
 * The ordering is by how specific the thing caught is. A drawn point is one
 * place and the person put it there, so it beats everything. A brick corner is
 * just as exact but was not drawn by hand, so it comes second. A midpoint is
 * one place but derived. A line and a brick edge are a whole locus rather than
 * a place, so they lose to anything exact within reach. The two axis holds are
 * last: they are the fallback that squares a drawing up, and being beaten by a
 * real feature is exactly what should happen.
 */
const PRIORITY: Readonly<Record<SketchSnapKind, number>> = {
  point: 0,
  'brick-corner': 1,
  midpoint: 2,
  line: 3,
  'brick-edge': 4,
  level: 5,
  plumb: 5,
};

/**
 * The best snap for a point, or null when nothing is within reach.
 *
 * The caller leaves out whatever is being moved: a vertex must not snap to
 * itself, and a shape being dragged must not catch its own edges.
 */
export function snapSketchPoint(
  x: Mm,
  y: Mm,
  toleranceMm: Mm,
  sources: SketchSnapSources,
): SketchSnap | null {
  if (!(toleranceMm > 0)) return null;

  let best: SketchSnap | null = null;
  let bestPriority = Infinity;
  let bestDistance = Infinity;

  const consider = (
    kind: SketchSnapKind,
    px: number,
    py: number,
    line?: SnapLine,
    reach = Math.hypot(px - x, py - y),
  ): void => {
    if (reach > toleranceMm) return;
    const priority = PRIORITY[kind];
    // Strictly better, so the first of two equals wins and the result is
    // stable however the sources happen to be ordered.
    if (priority > bestPriority) return;
    if (priority === bestPriority && reach >= bestDistance) return;
    bestPriority = priority;
    bestDistance = reach;
    best = { x: quantise(px), y: quantise(py), kind, line };
  };

  for (const p of sources.points ?? []) consider('point', p.x, p.y);
  for (const p of sources.brickCorners ?? []) consider('brick-corner', p.x, p.y);

  for (const line of sources.lines ?? []) {
    consider('point', line.a.x, line.a.y);
    consider('point', line.b.x, line.b.y);
    consider('midpoint', (line.a.x + line.b.x) / 2, (line.a.y + line.b.y) / 2, line);
    const foot = closestPointOnSegment(line.a, line.b, x, y);
    if (!isEnd(foot, line)) consider('line', foot.x, foot.y, line);
  }

  for (const edge of sources.brickEdges ?? []) {
    const foot = closestPointOnSegment(edge.a, edge.b, x, y);
    if (!isEnd(foot, edge)) consider('brick-edge', foot.x, foot.y, edge);
  }

  const from = sources.from;
  if (from != null) {
    // Measured along one axis only: a point three metres away is still level
    // with this one, and holding it there is the whole purpose.
    consider('level', x, from.y, undefined, Math.abs(y - from.y));
    consider('plumb', from.x, y, undefined, Math.abs(x - from.x));
  }

  return best;
}

/** Whether a foot of perpendicular is really just an end of the line. */
function isEnd(foot: SketchPoint, line: SnapLine): boolean {
  return (
    (Math.abs(foot.x - line.a.x) <= EPSILON && Math.abs(foot.y - line.a.y) <= EPSILON) ||
    (Math.abs(foot.x - line.b.x) <= EPSILON && Math.abs(foot.y - line.b.y) <= EPSILON)
  );
}
